import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import CommentItem, { CommentShape } from '../CommentItem/CommentItem';
import CommentForm from '../CommentForm/CommentForm';
import { CloseIcon } from '../Icons';
import { cx, getCommentText } from '../utils';
import styles from './CommentsModal.module.scss';

const confirm = Modal.confirm;

export default function CommentsModal({
  comments,
  currentUserId,
  maxLength,
  labels,
  allowAttachments,
  maxAttachments,
  acceptedFileTypes,
  formatAuthor,
  formatDate,
  isCommentEditable,
  confirmDelete,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onDownloadAttachment,
  getAttachmentUrl,
  onClose,
  className,
}) {
  const [editingComment, setEditingComment] = useState(null);
  const [text, setText] = useState('');
  const [newFiles, setNewFiles] = useState([]);
  const [keptAttachments, setKeptAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const resetForm = useCallback(() => {
    setEditingComment(null);
    setText('');
    setNewFiles([]);
    setKeptAttachments([]);
    setFormError('');
  }, []);

  const startEdit = useCallback((comment) => {
    setEditingComment(comment);
    setText(getCommentText(comment));
    setNewFiles([]);
    setKeptAttachments(comment.attachments || []);
    setFormError('');
  }, []);

  const handleAddFiles = useCallback(
    (files) => {
      setNewFiles((prev) => {
        const merged = [...prev, ...files];
        if (maxAttachments) {
          const room = Math.max(0, maxAttachments - keptAttachments.length);
          return merged.slice(0, room);
        }
        return merged;
      });
    },
    [maxAttachments, keptAttachments.length]
  );

  const handleRemoveNewFile = useCallback((index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveKeptAttachment = useCallback((id) => {
    setKeptAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleSubmit = useCallback(async () => {
    setFormError('');
    setSubmitting(true);
    try {
      if (editingComment) {
        const keepAttachmentIds = keptAttachments.map((a) => a.id);
        const removedAttachmentIds = (editingComment.attachments || [])
          .map((a) => a.id)
          .filter((id) => !keepAttachmentIds.includes(id));

        await onEditComment({
          id: editingComment.id,
          text,
          attachments: newFiles,
          keepAttachmentIds,
          removedAttachmentIds,
        });
      } else {
        await onAddComment({ text, attachments: newFiles });
      }
      resetForm();
    } catch (err) {
      setFormError((err && err.message) || labels.genericError);
    } finally {
      setSubmitting(false);
    }
  }, [
    editingComment,
    text,
    newFiles,
    keptAttachments,
    onAddComment,
    onEditComment,
    resetForm,
    labels.genericError,
  ]);

  const handleDelete = useCallback(
    async (comment) => {
      const proceed = confirmDelete
        ? await confirmDelete(comment)
        : await new Promise((resolve) => {
            confirm({
              title: labels.deleteConfirm,
              okText: labels.delete,
              okType: 'danger',
              cancelText: 'Отмена',
              zIndex: 1010,
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
      if (!proceed) return;

      setDeletingId(comment.id);
      try {
        await onDeleteComment(comment.id);
        if (editingComment && editingComment.id === comment.id) {
          resetForm();
        }
      } finally {
        setDeletingId(null);
      }
    },
    [
      confirmDelete,
      labels.deleteConfirm,
      labels.delete,
      onDeleteComment,
      editingComment,
      resetForm,
    ]
  );

  const handleOverlayMouseDown = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className={styles.overlay}
      onMouseDown={handleOverlayMouseDown}
      role="presentation"
    >
      <div
        className={cx(styles.modal, className)}
        role="dialog"
        aria-modal="true"
        aria-label={labels.modalTitle}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{labels.modalTitle}</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={labels.close}
            title={labels.close}
          >
            <CloseIcon />
          </button>
        </div>

        <ul className={styles.list}>
          {comments.length === 0 && (
            <li className={styles.empty}>{labels.noComments}</li>
          )}
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              labels={labels}
              formatAuthor={formatAuthor}
              formatDate={formatDate}
              isCommentEditable={isCommentEditable}
              isBeingEdited={
                editingComment ? editingComment.id === comment.id : false
              }
              onDownloadAttachment={onDownloadAttachment}
              getAttachmentUrl={getAttachmentUrl}
              onEdit={onEditComment ? startEdit : undefined}
              onDelete={onDeleteComment ? handleDelete : undefined}
              deleting={deletingId === comment.id}
            />
          ))}
        </ul>

        <CommentForm
          mode={editingComment ? 'edit' : 'create'}
          text={text}
          onTextChange={setText}
          maxLength={maxLength}
          labels={labels}
          allowAttachments={allowAttachments}
          newFiles={newFiles}
          onAddFiles={handleAddFiles}
          onRemoveNewFile={handleRemoveNewFile}
          keptAttachments={keptAttachments}
          onRemoveKeptAttachment={handleRemoveKeptAttachment}
          acceptedFileTypes={acceptedFileTypes}
          maxAttachments={maxAttachments}
          submitting={submitting}
          error={formError}
          onSubmit={handleSubmit}
          onCancelEdit={resetForm}
        />
      </div>
    </div>
  );
}

CommentsModal.propTypes = {
  comments: PropTypes.arrayOf(CommentShape).isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxLength: PropTypes.number.isRequired,
  labels: PropTypes.object.isRequired,
  allowAttachments: PropTypes.bool,
  maxAttachments: PropTypes.number,
  acceptedFileTypes: PropTypes.string,
  formatAuthor: PropTypes.func,
  formatDate: PropTypes.func,
  isCommentEditable: PropTypes.func,
  confirmDelete: PropTypes.func,
  onAddComment: PropTypes.func.isRequired,
  onEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  onDownloadAttachment: PropTypes.func,
  getAttachmentUrl: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  className: PropTypes.string,
};
